import { useRef } from 'react';
import { Download, FileCode, LoaderCircle, PackageOpen, Trash2, TriangleAlert, Upload } from 'lucide-react';
import Icon from './Icon';
import { confirmDialog, toast } from './dialogs';
import { absoluteAePackageUrl, type AePackage } from '../lib/aePackageApi';
import { AE_PACKAGE_ACCEPT, aePackageSizeText, aePackageStaleText } from '../lib/aePackage';
import type { AePackagesState } from '../lib/useAePackages';
import { formatDateTime } from '../lib/serverTime';
import './AePackagesPanel.css';

/**
 * 製作的「AE 打包檔」：每個版型一列（「全部」永遠是第一列，沒建版型的專案也有）。
 * 每列：下載腳本（照目前製作尺寸，用現有的下載）、上傳 / 換新、刪除，加上檔名 / 大小 / 上傳時間 / 進度，舊包會標出來。
 * 動畫師那邊看到的是 AePackageDownloads（Studio）。
 */

export interface AePackageTemplateRow { id: string; name: string; kind: 'full' | 'custom' }

interface Props {
  templates: readonly AePackageTemplateRow[];
  state: AePackagesState;
  /** 這一列的「下載腳本」（照目前製作尺寸）；做的時候回 'busy' 以外的都算結束 */
  onDownloadScript: (templateId: string) => void | Promise<void>;
  scriptBusyId: string | null;
  /** 一次下載所有版型的腳本（一個 zip，每個版型一個資料夾） */
  onDownloadAllScripts?: () => void;
  /** 做全部腳本時的進度文字；沒在做 = null */
  allScriptsText?: string | null;
}

export default function AePackagesPanel({ templates, state, onDownloadScript, scriptBusyId, onDownloadAllScripts, allScriptsText = null }: Props) {
  const { list, loaded, error, uploads } = state;
  return (
    <div className="aepk" data-testid="ae-packages-panel">
      <p className="aepk__intro">
        <Icon icon={PackageOpen} size="sm" /> 每個版型放一包做好的 AE 檔（壓縮檔），動畫師在被分配到的歌下載。「全部」是沒有排版型的整張畫布。
        換新的一包會通知用到這個版型的歌的負責動畫師。
      </p>
      {onDownloadAllScripts && list && (
        <div className="aepk__all">
          <button type="button" className="btn btn--ghost" onClick={onDownloadAllScripts} disabled={!!allScriptsText || !!scriptBusyId} data-testid="ae-all-scripts">
            <Icon icon={allScriptsText ? LoaderCircle : Download} spin={!!allScriptsText} /> 下載全部腳本
          </button>
          <span className="aepk__all-hint muted" aria-live="polite">{allScriptsText ?? '一個壓縮檔，每個版型一個資料夾，照目前的製作尺寸'}</span>
        </div>
      )}
      {!loaded && <p className="aepk__note muted">讀取中…</p>}
      {loaded && !list && !error && <p className="aepk__note muted">這個後台還沒有 AE 打包檔功能。</p>}
      {error && <p className="aepk__note aepk__note--warn" role="alert"><Icon icon={TriangleAlert} size="sm" /> {error}</p>}
      {list && (
        <ul className="aepk__list">
          {templates.map(t => (
            <AeRow key={t.id} tpl={t} state={state} onDownloadScript={onDownloadScript} scriptBusy={scriptBusyId === t.id || !!allScriptsText} />
          ))}
        </ul>
      )}
      {list && <p className="aepk__note muted">只收壓縮檔（.zip、.rar、.7z），一個檔案最多 {aePackageSizeText(list.maxBytes)}。{Object.keys(uploads).length > 0 ? '傳到一半請不要關掉這個頁面。' : ''}</p>}
    </div>
  );
}

function AeRow({ tpl, state, onDownloadScript, scriptBusy }: { tpl: AePackageTemplateRow; state: AePackagesState; onDownloadScript: Props['onDownloadScript']; scriptBusy: boolean }) {
  const pkg = state.packageOf(tpl.id);
  const up = state.uploads[tpl.id];
  const fileRef = useRef<HTMLInputElement>(null);
  const stale = pkg ? aePackageStaleText(pkg, pkg.stale, 'producer') : null;
  const name = tpl.kind === 'full' ? '全部' : tpl.name;

  async function pickFile(file: File | undefined) {
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    const err = await state.upload(tpl.id, file);
    if (err) toast.error(`「${name}」的 AE 打包檔沒有上傳成功：${err}`);
    else toast.success(`「${name}」的 AE 打包檔上傳好了`);
  }

  async function removeIt(p: AePackage) {
    if (!(await confirmDialog({
      title: `刪掉「${name}」的 AE 打包檔？`,
      message: `${p.filename} 會被刪除，動畫師就下載不到了（要再放一包的話重新上傳）。`,
      confirmLabel: '刪除', destructive: true,
    }))) return;
    const err = await state.remove(p);
    if (err) toast.error(err); else toast.success('已刪除');
  }

  const pct = up && up.total > 0 ? Math.min(100, Math.floor((up.loaded / up.total) * 100)) : 0;
  return (
    <li className="aepk__row" data-testid={`ae-row-${tpl.kind === 'full' ? 'full' : tpl.id}`}>
      <div className="aepk__name">
        <strong>{name}</strong>
        {tpl.kind === 'full' && <span className="muted"> 整張畫布</span>}
      </div>
      <div className="aepk__info">
        {up ? (
          <span className="aepk__progress" role="status">
            <Icon icon={LoaderCircle} size="sm" spin /> 上傳中 {up.filename}　{pct}%
            <span className="aepk__bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
          </span>
        ) : pkg ? (
          <>
            <span>{pkg.filename}</span>
            <span className="muted">{aePackageSizeText(pkg.sizeBytes)} · {formatDateTime(pkg.uploadedAt)}{pkg.uploadedBy?.name ? ` · ${pkg.uploadedBy.name}` : ''}</span>
            {stale && <span className="aepk__stale" role="status"><Icon icon={TriangleAlert} size="sm" /> {stale}</span>}
          </>
        ) : (
          <span className="muted">還沒有打包檔</span>
        )}
      </div>
      <div className="aepk__actions">
        <button className="btn btn--ghost" onClick={() => void onDownloadScript(tpl.id)} disabled={scriptBusy || !!up} title="下載 AE 腳本（照目前的製作尺寸）：自己在 AE 跑一次，做好合成再打包上傳">
          <Icon icon={scriptBusy ? LoaderCircle : FileCode} spin={scriptBusy} /> 下載腳本
        </button>
        <input ref={fileRef} type="file" accept={AE_PACKAGE_ACCEPT} hidden onChange={e => void pickFile(e.target.files?.[0])} aria-label={`選「${name}」的 AE 打包檔`} />
        <button className="btn btn--primary" onClick={() => fileRef.current?.click()} disabled={!!up} title="選一個壓縮檔（.zip、.rar、.7z）上傳">
          <Icon icon={Upload} /> {pkg ? '換新' : '上傳'}
        </button>
        {pkg && (
          <>
            <a className="btn btn--ghost" href={absoluteAePackageUrl(pkg.downloadUrl)} download={pkg.filename} title="下載現在放的這一包，看看是不是對的"><Icon icon={Download} /> 下載</a>
            <button className="btn btn--ghost aepk__danger" onClick={() => void removeIt(pkg)} disabled={!!up}><Icon icon={Trash2} /> 刪除</button>
          </>
        )}
      </div>
    </li>
  );
}
